import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { formatIDR } from "@/lib/constants";

/**
 * Weekly Expense Comparison Chart.
 * Groups entries by their week label (extracted from note pattern
 * "Pengeluaran minggu YYYY-Wxxx (...)") and renders horizontal bars.
 * Falls back to grouping by month for entries that don't match the
 * weekly pattern.
 */
export default function ExpenseWeeklyChart({ entries }) {
  const data = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    const buckets = new Map();
    // 2026-10-14: toleran 2-3 digit; normalisasi ke 2 digit agar bucket lama
    // (W038) & baru (W38) tergabung. Format kanonik = 2 digit.
    const weekRe = /^Pengeluaran minggu (\d{4})-W(\d{2,3})/;
    for (const e of entries) {
      const note = e.note || "";
      const match = weekRe.exec(note);
      let key;
      if (match) {
        key = `${match[1]}-W${String(parseInt(match[2], 10)).padStart(2, "0")}`;
      } else if (e.date) {
        const d = new Date(e.date);
        if (!Number.isNaN(d.getTime())) {
          key = `${d.getFullYear()}-M${String(d.getMonth() + 1).padStart(2, "0")}`;
        } else {
          key = "unknown";
        }
      } else {
        key = "unknown";
      }
      const prev = buckets.get(key) || { label: key, total: 0, count: 0 };
      prev.total += Number(e.amount) || 0;
      prev.count += 1;
      buckets.set(key, prev);
    }
    // sort ascending by label (label is lexicographically comparable)
    return Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [entries]);

  if (data.length === 0) return null;

  const maxTotal = Math.max(...data.map((d) => d.total));
  const avg = data.reduce((s, d) => s + d.total, 0) / data.length;

  return (
    <div className="border border-slate-200 p-4 space-y-2" data-testid="expense-weekly-chart">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-slate-600">Expense per Minggu · perbandingan</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {data.length} periode · rata-rata <span className="font-semibold text-slate-700 tabular-nums">{formatIDR(avg)}</span> · puncak <span className="font-semibold text-rose-700 tabular-nums">{formatIDR(maxTotal)}</span>
          </div>
        </div>
      </div>
      <div style={{ width: "100%", height: Math.max(160, data.length * 28) }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: "#475569", fontFamily: "monospace" }} width={110} />
            <Tooltip
              cursor={{ fill: "rgba(244, 63, 94, 0.08)" }}
              contentStyle={{ borderRadius: 0, border: "1px solid #e2e8f0", fontSize: 11 }}
              formatter={(value, _name, entry) => [`${formatIDR(value)} (${entry.payload.count} entri)`, "Total"]}
              labelStyle={{ fontWeight: 600, color: "#0B1D3A" }}
            />
            <Bar dataKey="total" radius={[0, 0, 0, 0]} label={{ position: "right", formatter: (v) => formatIDR(v), fontSize: 10, fill: "#334155" }}>
              {data.map((d) => (
                <Cell key={d.week || d.label} fill={d.total >= avg * 1.5 ? "#e11d48" : d.total >= avg ? "#f43f5e" : "#fda4af"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-slate-500 flex gap-3">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-rose-300" /> ≤ rata-rata</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-rose-500" /> ≥ rata-rata</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 bg-rose-700" /> ≥ 1.5× rata-rata (anomali)</span>
      </div>
    </div>
  );
}
